<template>
  <main>
    <ToastError ref="toastError" :text="toastErrorText" />

    <form novalidate @submit.prevent="authorizationUser">
      <h1>Вход в личный кабинет</h1>

      <div :class="style.mainForm">
        <div :class="style.inputBlock">
          <InputStyle
            placeholder="Введите почту"
            size="M"
            type="email"
            description="Email"
            errorText="Неверная почта"
            :isError="error"
            :disabled="isSubmitting"
            v-model="authorizationData.email"
          />

          <InputStyle
            placeholder="Введите пароль"
            size="M"
            type="password"
            description="Пароль"
            errorText="Неверный пароль"
            :isError="error"
            :disabled="isSubmitting"
            v-model="authorizationData.password"
          >
            <template #actions="{togglePassword, isPasswordVisible}">
              <ButtonIcon
                :icon="isPasswordVisible ? eyeOff : eyeOn"
                :disabled="isSubmitting"
                @click.stop.prevent="togglePassword"
              />
            </template>
          </InputStyle>
        </div>

        <a href="https://google.com">Восстановить пароль</a>
      </div>

      <PrimaryButton
        text="Войти"
        type="submit"
        :class="style.mainButton"
        :disabled="isSubmitting"
      />
    </form>

    <span :class="style.registerBlock">
      У вас нет аккаунта?
      <router-link to="registration">Зарегистрироваться</router-link>
    </span>
  </main>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import style from "../../StartPage.module.scss";

import InputStyle from "@/elements/UI/input/Style/InputStyle.vue";
import ToastError from "@/elements/UI/overlay/Toast/Error/ToastError.vue";
import PrimaryButton from "@/elements/UI/button/ButtonStyle/Default/primary/PrimaryButton.vue";

import eyeOn from "$images/icons/input/Name=Eye, Size=16, Tone=Default.svg";
import eyeOff from "$images/icons/input/Name=Eye-off, Size=16, Tone=Default.svg";
import ButtonIcon from "@/elements/UI/button/ButtonIcon/ButtonIcon.vue";
import {API} from "@/api/generated";

type AuthorizationData = {
  email: string;
  password: string;
};

export default defineComponent({
  name: "AuthorizationPage",

  components: {
    ButtonIcon,
    PrimaryButton,
    ToastError,
    InputStyle
  },

  data() {
    return {
      style,
      eyeOn,
      eyeOff,
      authorizationData: {
        email: "",
        password: ""
      } as AuthorizationData,
      error: false,
      errorResetTimer: null as number | null,
      isSubmitting: false,
      toastErrorText: "Неверный логин или пароль"
    };
  },

  created() {
    localStorage.clear();
  },

  beforeUnmount() {
    if (this.errorResetTimer !== null) {
      window.clearTimeout(this.errorResetTimer);
    }
  },

  methods: {
    async authorization(key: string): Promise<void> {
      try {
        const user = await API.getUser({
          headers: {
            Authorization: `Bearer ${key}`,
            Accept: "application/json"
          }
        });

        if (user?._id) {
          window.location.pathname = "/home";
        }
      } catch {
        this.showError();
      }
    },

    resetErrors(): void {
      this.error = false;
    },

    showError(text = "Неверный логин или пароль"): void {
      this.error = true;
      this.toastErrorText = text;

      const popup = this.popupRef;

      if (popup && typeof popup.show === "function") {
        popup.show();
      }

      if (this.errorResetTimer !== null) {
        window.clearTimeout(this.errorResetTimer);
      }

      this.errorResetTimer = window.setTimeout(() => {
        this.resetErrors();
        this.errorResetTimer = null;
      }, 3000);
    },

    async clearCaches(): Promise<void> {
      try {
        const keyList = await caches.keys();
        await Promise.all(keyList.map((key) => caches.delete(key)));
      } catch {}
    },

    validateForm(): boolean {
      this.resetErrors();

      const email = this.authorizationData.email.trim();
      const isValid =
        Boolean(email) &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
        Boolean(this.authorizationData.password);

      if (!isValid) {
        this.error = true;
      }

      return isValid;
    },

    async authorizationUser(): Promise<void> {
      if (this.isSubmitting) {
        return;
      }

      if (!this.validateForm()) {
        this.showError("Проверьте email и пароль");
        return;
      }

      this.isSubmitting = true;

      try {
        await this.clearCaches();

        const data = await API.authorization({
          body: {
            ...this.authorizationData,
            email: this.authorizationData.email.trim()
          }
        });

        if (typeof data === "string" && data.length > 0) {
          localStorage.setItem("keyUserAuth", data);
          await this.authorization(data);
        } else {
          this.showError();
        }
      } catch {
        this.showError();
      } finally {
        this.isSubmitting = false;
      }
    }
  },

  computed: {
    popupRef(): ToastType | null {
      return (
        (this.$refs.toastError as unknown as ToastType | undefined) ?? null
      );
    }
  },

  watch: {
    authorizationData: {
      handler(): void {
        this.resetErrors();
      },
      deep: true
    }
  }
});
</script>
